import React, { useEffect, useState } from 'react';
import Column from '@amzn/meridian/column';
import RadioButton from '@amzn/meridian/radio-button';
import Text from '@amzn/meridian/text';
import { FormattedMessage, useIntl } from 'react-intl';
import { RadioButtonType } from './formData.types';
import { grabSectionByID, grabRadioOptionsByID } from './helpers';
import { TextType } from '@amzn/meridian/text/text';

type RadioButtonValueType = React.ComponentProps<typeof RadioButton>['value'];

interface RadioButtonOption extends React.ComponentProps<typeof RadioButton> {
  label: string;
}

interface RadioButtonsFromDataProps
  extends React.ComponentProps<typeof RadioButton> {
  selectedValue: RadioButtonValueType;
  data: any;
  radioSectionID: string;
  setFormState: (...args: any) => void;
  styles: any;
  formState: any;
  handleSelectRadioValue: (t: string) => (checked: number) => void;
  formPrefix: string;
  'data-*'?: string;
  heading?: TextType;
}

const RadioButtonsFromData: React.FC<RadioButtonsFromDataProps> = ({
  ...props
}: RadioButtonsFromDataProps): React.ReactElement => {
  return (
    <div>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <BasicRadioButtonsFromData {...props} />
    </div>
  );
};

const BasicRadioButtonsFromData: React.FC<RadioButtonsFromDataProps> = ({
  name,
  selectedValue,
  onChange,
  disabled,
  data,
  radioSectionID,
  setFormState,
  styles,
  formState,
  handleSelectRadioValue,
  formPrefix,
  heading,
  ...props
}: RadioButtonsFromDataProps): React.ReactElement => {
  const intl = useIntl();

  useEffect(() => {
    const radioButtonSection = grabSectionByID(data, radioSectionID);
    const radioButtonItems = radioButtonSection?.QUESTIONS.map(question => ({
      type: question.QUESTION_ID.toLowerCase().replace(formPrefix, ''),
      placeholder: question.QUESTION_TEXT,
      options: grabRadioOptionsByID(radioButtonSection, question.QUESTION_ID),
      checked: selectedValue,
    }));

    // Adding a functional update form here because this was causing issues with CAP Form
    // There was a race condition between RadioButtonFormData and RenderDropdownsFormData setFormState calls
    setFormState((prevState: any) => ({ ...prevState, radioButtonItems }));
  }, []);

  return (
    <>
      {(formState?.radioButtonItems ?? []).map(
        (optionsData: RadioButtonType) => {
          return optionsData.options.length > 0 ? (
            <Column spacingInset='300 none' spacing='300' height='135px'>
              <Text type={heading}>
                {intl.formatMessage({
                  id: optionsData.placeholder.TRANSLATION_ID,
                  defaultMessage: optionsData.placeholder.DEFAULT_MESSAGE,
                })}
              </Text>
              {optionsData.options.map(option => (
                <RadioButton
                  disabled={disabled}
                  value={option.VALUE}
                  name={optionsData.type}
                  key={`${name}_value_${option.VALUE}`}
                  checked={option.VALUE === optionsData.checked}
                  onChange={handleSelectRadioValue(optionsData.type)}
                >
                  <FormattedMessage
                    id={option.TRANSLATION_ID}
                    defaultMessage={option.DEFAULT_MESSAGE}
                  />
                </RadioButton>
              ))}
            </Column>
          ) : (
            <Column spacingInset='300 none' spacing='300'>
              <Text type='h300'>
                {intl.formatMessage({
                  id: optionsData.placeholder.TRANSLATION_ID,
                  defaultMessage: optionsData.placeholder.DEFAULT_MESSAGE,
                })}
              </Text>
            </Column>
          );
        }
      )}
    </>
  );
};

export default RadioButtonsFromData;
